import { Holding, normalizeWatchlistSymbol, Watchlist } from '@benzinga/watchlist-manager';
import {
  AggregatedHolding,
  DailyChangeLeader,
  DailyChangePosition,
  DailyChangeQuoteInput,
  DailyChangeResult,
  PortfolioHoldingValues,
  PricedHolding,
} from './entity';

export const getHoldingQuantity = (holdings: Holding | Holding[] | null | undefined): number =>
  Array.isArray(holdings)
    ? holdings.reduce((total, holding) => total + (holding.quantity ?? 0), 0)
    : (holdings?.quantity ?? 0);

export const hasLinkedHoldings = (watchlists: Pick<Watchlist, 'symbols'>[]): boolean =>
  watchlists.some(watchlist => watchlist.symbols.some(symbol => getHoldingQuantity(symbol.holding) > 0));

export const aggregateHoldingsBySymbol = (watchlists: Pick<Watchlist, 'symbols'>[]): AggregatedHolding[] => {
  const holdingsBySymbol = new Map<string, AggregatedHolding>();

  watchlists.forEach(watchlist => {
    watchlist.symbols.forEach(symbol => {
      const holdings = Array.isArray(symbol.holding) ? symbol.holding : symbol.holding ? [symbol.holding] : [];
      holdings.forEach(holding => {
        if (holding.quantity <= 0) return;
        const current = holdingsBySymbol.get(symbol.symbol) ?? {
          costBasis: 0,
          fallbackPrice: 0,
          quantity: 0,
          symbol: symbol.symbol,
        };
        const nextQuantity = current.quantity + holding.quantity;
        const fallbackContribution =
          holding.price > 0 ? holding.price : holding.quantity > 0 ? holding.costBasis / holding.quantity : 0;
        if (fallbackContribution > 0 && nextQuantity > 0) {
          current.fallbackPrice =
            (current.fallbackPrice * current.quantity + fallbackContribution * holding.quantity) / nextQuantity;
        }
        current.costBasis += holding.costBasis;
        current.quantity = nextQuantity;
        holdingsBySymbol.set(symbol.symbol, current);
      });
    });
  });

  return Array.from(holdingsBySymbol.values());
};

export const priceHoldings = (
  holdings: AggregatedHolding[],
  pricesBySymbol: Record<string, number>,
  companyNamesBySymbol: Record<string, string> = {},
  normalizeSymbol: (symbol: string) => string = normalizeWatchlistSymbol
): PricedHolding[] =>
  holdings
    .map(holding => {
      const quotePrice = pricesBySymbol[holding.symbol] ?? pricesBySymbol[normalizeSymbol(holding.symbol)] ?? 0;
      const price = quotePrice > 0 ? quotePrice : holding.fallbackPrice > 0 ? holding.fallbackPrice : 0;
      const companyName = companyNamesBySymbol[holding.symbol] ?? companyNamesBySymbol[normalizeSymbol(holding.symbol)];
      return {
        ...holding,
        companyName,
        marketValue: price > 0 ? holding.quantity * price : 0,
        price,
      };
    })
    .sort((firstHolding, secondHolding) => secondHolding.marketValue - firstHolding.marketValue);

export const sumPricedHoldingValues = (holdings: PricedHolding[]): PortfolioHoldingValues => {
  let costBasis = 0;
  let totalValue = 0;
  let pricedCount = 0;
  let unpricedCount = 0;
  const symbolValues = new Map<string, number>();

  holdings.forEach(holding => {
    if (holding.price <= 0 || holding.marketValue <= 0) {
      unpricedCount += 1;
      return;
    }
    pricedCount += 1;
    costBasis += holding.costBasis;
    totalValue += holding.marketValue;
    symbolValues.set(holding.symbol, holding.marketValue);
  });

  return { costBasis, pricedCount, symbolValues, totalValue, unpricedCount };
};

export const getHistoryChartWeight = (holdings: Holding | Holding[] | null | undefined): number =>
  getHoldingQuantity(holdings) || 1;

export const getHoldingCostBasis = (holdings: Holding | Holding[] | null | undefined): number =>
  Array.isArray(holdings)
    ? holdings.reduce((total, holding) => total + (holding.costBasis ?? 0), 0)
    : (holdings?.costBasis ?? 0);

export const buildDailyChangePosition = (input: DailyChangeQuoteInput): DailyChangePosition | null => {
  const hasHolding = input.quantity > 0;
  const changePercent =
    typeof input.changePercent === 'number' && Number.isFinite(input.changePercent) ? input.changePercent : null;

  if (!hasHolding && changePercent === null) return null;

  const previousClose =
    typeof input.previousClosePrice === 'number' && input.previousClosePrice > 0 ? input.previousClosePrice : null;
  const lastTrade = typeof input.lastTradePrice === 'number' && input.lastTradePrice > 0 ? input.lastTradePrice : null;
  const priceForWeight = previousClose ?? lastTrade;
  const costBasis = input.costBasis ?? 0;

  let marketValue = 0;
  if (hasHolding) {
    if (priceForWeight != null) {
      marketValue = input.quantity * priceForWeight;
    } else if (costBasis > 0) {
      marketValue = costBasis;
    }
  }

  return {
    changePercent: changePercent ?? 0,
    marketValue,
    ...(input.symbol ? { symbol: input.symbol } : {}),
  };
};

export const resolveDailyChangePercent = (positions: DailyChangePosition[]): DailyChangeResult | null => {
  const withChange = positions.filter(position => Number.isFinite(position.changePercent));
  if (withChange.length === 0) return null;

  const heldPositions = withChange.filter(position => position.marketValue > 0);
  const totalMarketValue = heldPositions.reduce((total, position) => total + position.marketValue, 0);

  if (totalMarketValue > 0) {
    const weightedSum = heldPositions.reduce(
      (total, position) => total + position.changePercent * position.marketValue,
      0,
    );
    const breadth = countSignedMoves(heldPositions);
    return {
      ...breadth,
      leader: pickDailyChangeLeader(
        heldPositions,
        position => (position.changePercent * position.marketValue) / totalMarketValue,
      ),
      percent: weightedSum / totalMarketValue,
      weighting: 'portfolio',
    };
  }

  const equalWeight = averageChangePercent(withChange.map(position => position.changePercent));
  if (equalWeight === null) return null;
  const breadth = countSignedMoves(withChange);
  return {
    ...breadth,
    leader: pickDailyChangeLeader(withChange, position => position.changePercent),
    percent: equalWeight,
    weighting: 'watchlist',
  };
};

export const averageChangePercent = (changes: number[]): number | null => {
  const values = changes.filter(value => Number.isFinite(value));
  if (values.length === 0) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
};

export const countSignedMoves = (source: DailyChangePosition[]): Pick<DailyChangeResult, 'downCount' | 'upCount'> => ({
  downCount: source.reduce((total, position) => total + (position.changePercent < 0 ? 1 : 0), 0),
  upCount: source.reduce((total, position) => total + (position.changePercent > 0 ? 1 : 0), 0),
});

export const pickDailyChangeLeader = (
  source: DailyChangePosition[],
  scoreFor: (position: DailyChangePosition) => number,
): DailyChangeLeader | null => {
  let leader: DailyChangeLeader | null = null;
  let bestScore = 0;

  source.forEach(position => {
    if (!position.symbol) return;
    const score = Math.abs(scoreFor(position));
    if (score <= bestScore) return;
    bestScore = score;
    leader = { changePercent: position.changePercent, symbol: position.symbol };
  });

  return leader;
};
